import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import type { RefundRequestListItemDTO } from "../api/client";

const STATUS_LABELS: Record<string, string> = {
  requested: "ລໍຖ້າກວດສອບ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
  refunded: "ຄືນເງິນແລ້ວ",
  closed: "ປິດ",
};

type RefundRequestsTableProps = {
  isLoading: boolean;
  data: RefundRequestListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (id: string) => void;
};

export function RefundRequestsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
  onView,
}: RefundRequestsTableProps) {
  const columns: TanstackReactTable.ColumnDef<RefundRequestListItemDTO>[] = [
    createSortableColumn<RefundRequestListItemDTO>("requestedAt", "ວັນທີຂໍ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.requestedAt)}
        </span>
      ),
    }),
    createSortableColumn<RefundRequestListItemDTO>("studentName", "ນັກຮຽນ", {
      size: 180,
      cell: ({ row }) => (
        <span>{row.original.studentName ?? "-"}</span>
      ),
    }),
    createSortableColumn<RefundRequestListItemDTO>("packageName", "ແພັກເກດ", {
      size: 200,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.packageName ?? "-"}
        </span>
      ),
    }),
    createSortableColumn<RefundRequestListItemDTO>("refundAmount", "ຈຳນວນເງິນ", {
      size: 120,
      cell: ({ row }) => (
        <span className="font-semibold">
          {row.original.refundAmount.toLocaleString("lo-LA")} ກີບ
        </span>
      ),
    }),
    createSortableColumn<RefundRequestListItemDTO>("status", "ສະຖານະ", {
      size: 140,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {STATUS_LABELS[row.original.status] ?? row.original.status}
        </span>
      ),
    }),
    {
      id: "actions",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="size-4" />,
              onClick: () => onView(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<RefundRequestListItemDTO, unknown>
      noDataMessage="ບໍ່ພົບຄຳຂໍຄືນເງິນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting || sorting.length === 0) return;
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
      }}
    />
  );
}
